import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';

import { useAuth } from '../../contexts/AuthContext';
import { 
  getMeetingById, 
  getMeetingParticipants, 
  getParticipantLocations,
  getParticipantStatuses,
  getUserProfile,
  subscribeToParticipantLocations,
  subscribeToParticipantStatuses
} from '../../services/meetingService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LiveMap from './components/LiveMap';
import ParticipantCard from './components/ParticipantCard';
import EmergencyContactModal from './components/EmergencyContactModal';
import { trackActiveMeetingView, trackEmergencyContactAccess, trackEngagementTime } from '../../utils/analytics';

const ActiveMeeting = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const meetingId = searchParams?.get('meetingId');

  const [meeting, setMeeting] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [locations, setLocations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [pageLoadTime, setPageLoadTime] = useState(null);

  useEffect(() => {
    setPageLoadTime(Date.now());
    
    return () => {
      // Track engagement time when leaving page
      if (pageLoadTime) {
        const timeSpent = Math.floor((Date.now() - pageLoadTime) / 1000);
        trackEngagementTime('Active Meeting', timeSpent);
      }
    };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/signin');
      return;
    }

    if (!meetingId) {
      setError('No meeting ID provided');
      setLoading(false);
      return;
    }

    loadMeetingData();
  }, [meetingId, user, authLoading, navigate]);

  useEffect(() => {
    if (!meetingId) return;

    const unsubscribeLocations = subscribeToParticipantLocations(meetingId, () => {
      loadLocations();
    });

    const unsubscribeStatuses = subscribeToParticipantStatuses(meetingId, () => {
      loadStatuses();
    });

    return () => {
      unsubscribeLocations();
      unsubscribeStatuses();
    };
  }, [meetingId]);

  const loadMeetingData = async () => {
    try {
      setLoading(true);
      const [meetingData, participantsData, locationsData, statusesData] = await Promise.all([
        getMeetingById(meetingId),
        getMeetingParticipants(meetingId),
        getParticipantLocations(meetingId),
        getParticipantStatuses(meetingId)
      ]);

      if (!meetingData) {
        setError('Meeting not found');
        return;
      }

      setMeeting(meetingData);
      setParticipants(participantsData || []);
      setLocations(locationsData || []);
      setStatuses(statusesData || []);
      
      // Track active meeting view
      trackActiveMeetingView(meetingId, participantsData?.length || 0);

      // Load user profiles for emergency contacts
      const profilePromises = (participantsData || [])?.map(p => getUserProfile(p?.userId));
      const profiles = await Promise.all(profilePromises);
      const profilesMap = {};
      profiles?.forEach(profile => {
        if (profile) {
          profilesMap[profile?.id] = profile;
        }
      });
      setUserProfiles(profilesMap);
    } catch (err) {
      setError(err?.message || 'Failed to load meeting');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const locationsData = await getParticipantLocations(meetingId);
      setLocations(locationsData || []);
    } catch (err) {
      console.error('Failed to reload locations:', err);
    }
  };

  const loadStatuses = async () => {
    try {
      const statusesData = await getParticipantStatuses(meetingId);
      setStatuses(statusesData || []);
    } catch (err) {
      console.error('Failed to reload statuses:', err);
    }
  };

  const loadParticipants = async () => {
    try {
      const participantsData = await getMeetingParticipants(meetingId);
      setParticipants(participantsData || []);
      
      // Show toast notification when new participant joins
      if (participantsData?.length > participants?.length) {
        const newParticipant = participantsData?.[participantsData?.length - 1];
        toast?.info(`${newParticipant?.participantName} has joined the meeting!`, {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (err) {
      console.error('Failed to reload participants:', err);
    }
  };

  const handleShowEmergencyContact = (participant) => {
    setSelectedParticipant(participant);
    setShowEmergencyModal(true);
    
    // Track emergency contact access
    trackEmergencyContactAccess(meetingId);
  };

  const getParticipantLocation = (userId) => {
    return locations?.find(loc => loc?.userId === userId);
  };

  const getParticipantStatus = (userId) => {
    return statuses?.find(status => status?.userId === userId);
  };

  const calculateArrivalTime = (participant) => {
    const now = new Date();
    const arrivalMinutes = participant?.travelTime || 0;
    return new Date(now?.getTime() + arrivalMinutes * 60000);
  };

  const countdownRenderer = ({ hours, minutes, seconds, completed }) => {
    if (completed) {
      return <span className="text-success font-semibold">Arrived!</span>;
    }
    return (
      <span className="text-foreground font-semibold">
        {hours > 0 && `${hours}h `}{minutes}m {seconds}s
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading active meeting...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-destructive" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">Error</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/')}>Go to Home</Button>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return null;
  }

  const meetingLocation = {
    lat: meeting?.locationLat,
    lng: meeting?.locationLng,
    name: meeting?.locationName
  };

  const participantLocations = participants?.map(p => {
    const location = getParticipantLocation(p?.userId);
    return {
      userId: p?.userId,
      name: p?.participantName,
      lat: location?.latitude || 0,
      lng: location?.longitude || 0,
      hasLocation: !!location
    };
  });

  return (
    <>
      <Helmet>
        <title>Active Meeting - Mosey</title>
        <meta name="description" content="Track participants in real-time as they travel to your meeting location." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Navigation" size={24} className="text-success" />
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                    Active Meeting
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground mt-1">
                    {meeting?.locationName}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate(`/final-meeting-details?meetingId=${meetingId}`)}
              >
                Back to Details
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="MapPin" size={18} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Meeting Location</span>
              </div>
              <p className="text-sm text-muted-foreground">{meeting?.locationAddress}</p>
              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{meeting?.meetingDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{meeting?.meetingTime}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-lg p-6 shadow-md mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="Map" size={24} className="text-primary" />
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
                    Live Participant Locations
                  </h2>
                </div>
                <div className="h-[400px] md:h-[500px] rounded-lg overflow-hidden">
                  <LiveMap
                    meetingLocation={meetingLocation}
                    participantLocations={participantLocations}
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-6 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="Users" size={24} className="text-primary" />
                  <h2 className="font-heading text-xl font-semibold text-foreground">
                    Participants ({participants?.length})
                  </h2>
                </div>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {participants?.map((participant) => {
                    const location = getParticipantLocation(participant?.userId);
                    const status = getParticipantStatus(participant?.userId);
                    const profile = userProfiles?.[participant?.userId];
                    const arrivalTime = calculateArrivalTime(participant);

                    return (
                      <ParticipantCard
                        key={participant?.id}
                        participant={participant}
                        location={location}
                        status={status}
                        profile={profile}
                        currentUserId={user?.id}
                        arrivalTime={arrivalTime}
                        countdownRenderer={countdownRenderer}
                        onShowEmergencyContact={() => handleShowEmergencyContact(participant)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {showEmergencyModal && selectedParticipant && (
        <EmergencyContactModal
          participant={selectedParticipant}
          profile={userProfiles?.[selectedParticipant?.userId]}
          onClose={() => {
            setShowEmergencyModal(false);
            setSelectedParticipant(null);
          }}
        />
      )}
    </>
  );
};

export default ActiveMeeting;